import React, { useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { Copy, Check, Zap, DollarSign, Database, Shield } from 'lucide-react';

const X402ApiDocs: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      slug: 'general',
      name: 'Complete Professional Database',
      description: 'Access the entire professional license database across all categories',
      price: '0.03',
      recordCount: '13M+',
      filters: 'All professions, all states'
    },
    {
      slug: 'real-estate',
      name: 'Real Estate Professionals',
      description: 'Licensed real estate agents, brokers, and salespersons',
      price: '0.03',
      recordCount: '680K+',
      filters: 'Real Estate category'
    },
    {
      slug: 'vehicle-transporter',
      name: 'Vehicle Transporters',
      description: 'Licensed vehicle and motor vehicle dealers',
      price: '0.03',
      recordCount: '175K+',
      filters: 'Transportation category'
    },
    {
      slug: 'cannabis',
      name: 'Cannabis License Holders',
      description: 'Adult use cannabis business licenses',
      price: '0.03',
      recordCount: '140K+',
      filters: 'Cannabis category'
    },
    {
      slug: 'acupuncture',
      name: 'Healthcare Professionals',
      description: 'Licensed acupuncture and health professionals',
      price: '0.03',
      recordCount: '35K+',
      filters: 'Health/Medical category'
    }
  ];

  const generalExample = `curl -X POST \\
  'https://progeodata-com.auramediastudios.workers.dev/api/x402/search' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "state": "FL",
    "city": "Miami",
    "limit": 100
  }'`;

  const categoryExample = `curl -X POST \\
  'https://progeodata-com.auramediastudios.workers.dev/api/x402/leads/real-estate' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "state": "FL",
    "city": "Tampa",
    "status": "active",
    "limit": 50
  }'`;

  const paymentResponse = `{
  "error": "Payment Required",
  "payment_instructions": {
    "price": "0.03",
    "currency": "USDC",
    "network": "base",
    "pay_to": "0x02F68718AfbB3A8C4BDF906476CB444d7fe551Bd",
    "metadata": {
      "description": "Verified Real Estate leads with direct contact info",
      "product_name": "Real Estate Leads (FL)",
      "available_records": 680542,
      "inputSchema": {
        "type": "object",
        "properties": {
          "state": { "type": "string", "description": "2-letter State Code" },
          "city": { "type": "string", "description": "City Filter" },
          "zip": { "type": "string", "description": "Zip Code Filter" },
          "status": { "type": "string", "enum": ["active", "all"] },
          "limit": { "type": "number", "default": 100 }
        },
        "required": ["state"]
      }
    }
  }
}`;

  const successResponse = `{
  "count": 50,
  "results": [
    {
      "name": "John Smith",
      "email": "john.smith@example.com",
      "phone": "+1-305-555-0123",
      "license_number": "BK123456",
      "specialty": "Real Estate Broker",
      "address": "123 Main St, Miami, FL 33101",
      "city": "Miami",
      "state": "FL",
      "zip": "33101",
      "status": "active",
      "issue_date": "2020-01-15",
      "expiration_date": "2026-01-15"
    }
  ]
}`;

  const paidRequest = `curl -X POST \\
  'https://progeodata-com.auramediastudios.workers.dev/api/x402/leads/real-estate' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: x402 <payment_proof_token>' \\
  -d '{
    "state": "FL",
    "city": "Miami",
    "limit": 50
  }'`;

  return (
    <div className="bg-slate-900 min-h-screen text-slate-300 font-sans antialiased">
      <Navbar />

      {/* Hero Section */}
      <div className="border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400 text-sm font-bold mb-6 uppercase gap-2">
              <Zap className="w-4 h-4" />
              x402 Payment Protocol
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Pay-Per-Request API
            </h1>
            <p className="text-xl text-slate-400 mb-8">
              Access professional license data with crypto micropayments.
              No subscriptions. No API keys. Pay only for what you use.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                <Database className="w-8 h-8 text-sky-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">13M+</div>
                <div className="text-sm text-slate-400">License Records</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                <DollarSign className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">$0.03</div>
                <div className="text-sm text-slate-400">Per API Request</div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6">
                <Shield className="w-8 h-8 text-green-400 mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">USDC</div>
                <div className="text-sm text-slate-400">Base Network</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">

        {/* What is x402? */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">What is x402?</h2>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-8">
            <p className="text-lg text-slate-300 mb-4">
              x402 is a payment protocol for HTTP APIs that enables <strong className="text-white">crypto micropayments</strong> on a per-request basis.
              Instead of managing API keys, subscriptions, and billing, you simply pay for each request in USDC on the Base network.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">How it works:</h3>
                <ol className="space-y-2 text-slate-300">
                  <li className="flex gap-2">
                    <span className="text-amber-400 font-bold">1.</span>
                    Make an API request without payment
                  </li>
                  <li className="flex gap-2">
                    <span className="text-amber-400 font-bold">2.</span>
                    Receive 402 Payment Required response with payment details
                  </li>
                  <li className="flex gap-2">
                    <span className="text-amber-400 font-bold">3.</span>
                    Send USDC to the provided wallet address
                  </li>
                  <li className="flex gap-2">
                    <span className="text-amber-400 font-bold">4.</span>
                    Retry request with payment proof token
                  </li>
                </ol>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">Benefits:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex gap-2">
                    <span className="text-green-400">✓</span>
                    No API key management
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-400">✓</span>
                    Pay only for successful requests
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-400">✓</span>
                    Instant settlement on Base L2
                  </li>
                  <li className="flex gap-2">
                    <span className="text-green-400">✓</span>
                    No subscriptions or commitments
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Available Endpoints */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Available Endpoints</h2>
          <div className="space-y-4">
            {endpoints.map((endpoint) => (
              <div key={endpoint.slug} className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 hover:border-slate-600 transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1">{endpoint.name}</h3>
                    <p className="text-slate-400">{endpoint.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-amber-400">${endpoint.price}</div>
                    <div className="text-xs text-slate-500">per request</div>
                  </div>
                </div>
                <div className="flex gap-6 text-sm mt-4 pt-4 border-t border-slate-700">
                  <div>
                    <span className="text-slate-500">Records: </span>
                    <span className="text-sky-400 font-medium">{endpoint.recordCount}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Endpoint: </span>
                    <code className="text-green-400 font-mono text-xs">
                      /api/x402/leads/{endpoint.slug}
                    </code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* API Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">API Examples</h2>

          {/* Step 1: Initial Request */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="bg-amber-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">1</span>
              Make Initial Request (No Payment)
            </h3>
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <div className="bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">POST /api/x402/leads/real-estate</div>
                </div>
                <button
                  onClick={() => handleCopy(categoryExample, 'category')}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {copiedCode === 'category' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <pre className="text-sm font-mono text-slate-300">{categoryExample}</pre>
              </div>
            </div>
          </div>

          {/* Step 2: Payment Required Response */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="bg-amber-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">2</span>
              Receive Payment Instructions (402 Response)
            </h3>
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <div className="bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">HTTP 402 Payment Required</div>
                </div>
                <button
                  onClick={() => handleCopy(paymentResponse, 'payment')}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {copiedCode === 'payment' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <pre className="text-sm font-mono text-slate-300">{paymentResponse}</pre>
              </div>
            </div>
            <div className="mt-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
              <p className="text-sm text-amber-300">
                <strong>Note:</strong> The response includes the wallet address, price, network details,
                and available record count. Use this information to complete your payment.
              </p>
            </div>
          </div>

          {/* Step 3: Payment & Retry */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="bg-amber-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">3</span>
              Send Payment & Retry Request
            </h3>
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <div className="bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">POST with x402 Authorization</div>
                </div>
                <button
                  onClick={() => handleCopy(paidRequest, 'paid')}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {copiedCode === 'paid' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <pre className="text-sm font-mono text-slate-300">{paidRequest}</pre>
              </div>
            </div>
          </div>

          {/* Step 4: Success Response */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="bg-green-500 text-slate-900 w-8 h-8 rounded-full flex items-center justify-center font-bold">4</span>
              Receive Data (200 Success)
            </h3>
            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
              <div className="bg-slate-900 p-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">HTTP 200 OK</div>
                </div>
                <button
                  onClick={() => handleCopy(successResponse, 'success')}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {copiedCode === 'success' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="p-6 overflow-x-auto">
                <pre className="text-sm font-mono text-slate-300">{successResponse}</pre>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Details */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Payment Details</h2>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Network Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Network:</span>
                    <span className="text-white font-mono">Base L2</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Currency:</span>
                    <span className="text-white font-mono">USDC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Wallet Address:</span>
                    <code className="text-green-400 font-mono text-xs">0x02F6...51Bd</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Settlement:</span>
                    <span className="text-white">Instant</span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Pricing</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Per Request:</span>
                    <span className="text-amber-400 font-bold">$0.03 USDC</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Network Fees:</span>
                    <span className="text-white">~$0.001</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Total Cost:</span>
                    <span className="text-white font-bold">~$0.031 USDC</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-4">
                    * Network fees vary based on Base network congestion
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Query Parameters */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">Query Parameters</h2>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900">
                <tr>
                  <th className="text-left p-4 text-white font-semibold border-b border-slate-700">Parameter</th>
                  <th className="text-left p-4 text-white font-semibold border-b border-slate-700">Type</th>
                  <th className="text-left p-4 text-white font-semibold border-b border-slate-700">Required</th>
                  <th className="text-left p-4 text-white font-semibold border-b border-slate-700">Description</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                <tr className="border-b border-slate-700">
                  <td className="p-4 font-mono text-sm">state</td>
                  <td className="p-4">string</td>
                  <td className="p-4"><span className="text-red-400">Yes</span></td>
                  <td className="p-4">2-letter state code (e.g., "FL", "NY", "CA")</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="p-4 font-mono text-sm">city</td>
                  <td className="p-4">string</td>
                  <td className="p-4"><span className="text-slate-500">No</span></td>
                  <td className="p-4">Filter by city name</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="p-4 font-mono text-sm">zip</td>
                  <td className="p-4">string</td>
                  <td className="p-4"><span className="text-slate-500">No</span></td>
                  <td className="p-4">Filter by zip code</td>
                </tr>
                <tr className="border-b border-slate-700">
                  <td className="p-4 font-mono text-sm">status</td>
                  <td className="p-4">string</td>
                  <td className="p-4"><span className="text-slate-500">No</span></td>
                  <td className="p-4">License status: "active" or "all" (default: "all")</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">limit</td>
                  <td className="p-4">number</td>
                  <td className="p-4"><span className="text-slate-500">No</span></td>
                  <td className="p-4">Max records to return (default: 100, max: 1000)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Support Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-sky-900/20 to-slate-900/20 border border-sky-700/50 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need Help?</h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Have questions about x402 integration or need assistance setting up payments?
              Our team is here to help you get started.
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="mailto:support@progeodata.com"
                className="inline-flex items-center px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-lg transition-colors"
              >
                Contact Support
              </a>
              <a
                href="/"
                className="inline-flex items-center px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors border border-slate-700"
              >
                View Pricing
              </a>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default X402ApiDocs;
